import { calculateLineItem, calculateFormTotals, calculateDisplayTotals } from "common/utils/lineItemCalc";
import { useCustomForm } from "common/hooks/src/api/useCustomForm";
import { Button } from "components/button";
import { CardWithTitle } from "components/card/CardWithTitle";
import { InputField, ResizableInputField } from "components/fields/InputField";
import { useGetInvoice, useUpdateInvoice } from "modules/invoice/api";
import {
  CreateInvoicePayload,
  ReductionType,
  InvoiceLineItem,
  INVOICE_STATUS,
  InvoiceTransaction,
} from "modules/invoice/types/invoice.interface";
import { FormEvent, useEffect, useState } from "react";
import { useDocumentPreview } from "common/hooks/src/useDocumentPreview";
import "react-datepicker/dist/react-datepicker.css";
import { useParams } from "react-router-dom";
import { useGetArticles } from "modules/article/api";
import {
  SelectDevis,
  SelectIntervenants,
  SelectClient,
  SelectStatus,
  SelectArticle,
  SelectReductionType,
  TransactionHistory,
  PaymentModal,
} from "modules/invoice/components";
import { Mail, PlusCircle } from "lucide-react";
import { SendEmailModal } from "modules/devis/components/src/sendEmailDevis";

const UpdateInvoice = () => {
  const { id } = useParams<{ id: string }>();
  const { data: invoice } = useGetInvoice(id);
  const { mutate } = useUpdateInvoice(id);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const { data: articlesData } = useGetArticles(undefined);

  const { formValues, setFormValues } = useCustomForm<CreateInvoicePayload>({
    clientId: "",
    dueDate: null,
    interveners: [],
    status: INVOICE_STATUS.DRAFT,
    lineItems: [
      {
        articleId: "",
        priceHT: 0,
        quantity: 0,
        vatAmount: 0,
        reductionAmount: 0,
        reductionType: ReductionType.PERCENTAGE,
        totalTTC: 0,
        totalHT: 0,
        description: "",
      },
    ],
    totalTTC: 0,
    totalHT: 0,
    transactions: [],
  });

  const isSubmitDisabled = false;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isSubmitDisabled) return;
    mutate(formValues);
  };

  useEffect(() => {
    if (!invoice) return;

    const allArticles = articlesData
      ? articlesData.pages.flatMap((page) => page.data)
      : [];

    const updatedLineItems = invoice.lineItems.map((item) => {
      const matchedArticle = allArticles.find(
        (a) => a._id === item.articleId
      );

      return {
        ...item,
        type: matchedArticle?.type,
        description: item.description ?? "",
        priceTTC: item.totalTTC,
        quantity: Number(item.quantity) || 0,
        vatAmount: Number(item.vatAmount) || 0,
        reductionAmount: Number(item.reductionAmount) || 0,
        reductionType: item.reductionType || ReductionType.PERCENTAGE,
        totalTTC: Number(item.totalTTC) || 0,
        totalHT: Number(item.totalHT) || 0,
        priceHT: (() => {
          const stored = Number((item as any).unitPriceHT) || Number(item.priceHT) || 0;
          if (stored > 0) return stored;
          const qty = Number(item.quantity);
          const ht = Number(item.totalHT);
          return !Number(item.reductionAmount) && ht > 0 && qty > 0 ? ht / qty : 0;
        })(),
      };
    });

    setFormValues({
      ...invoice,
      dueDate: new Date(invoice.dueDate),
      interveners: invoice.interveners || [],
      lineItems: updatedLineItems,
      transactions: invoice.transactions || [],
      totalTTC: Number(invoice.totalTTC),
      totalHT: Number(invoice.totalHT),
    });
  }, [invoice, articlesData, setFormValues]);

  const handleAddLineItem = () => {
    setFormValues({
      ...formValues,
      lineItems: [
        ...formValues.lineItems,
        {
          articleId: "",
          quantity: 1,
          reductionType: ReductionType.PERCENTAGE,
          reductionAmount: 0,
          vatAmount: 20,
          priceHT: 0,
          description: "",
          totalTTC: 0,
          totalHT: 0,
        },
      ],
    });
  };

  const handleRemoveLineItem = (index: number) => {
    const updatedLineItems = formValues.lineItems.filter(
      (_, idx) => idx !== index
    );
    const totals = calculateFormTotals(updatedLineItems);
    setFormValues({
      ...formValues,
      lineItems: updatedLineItems,
      totalTTC: totals.totalTTC,
      totalHT: totals.totalHT,
    });
  };

  const handleLineItemChange = (
    index: number,
    field: keyof InvoiceLineItem,
    value: any
  ) => {
    const updatedLineItems = formValues.lineItems.map((item, idx) => {
      if (idx === index) {
        const newItem = { ...item, [field]: value };

        if (field === "articleId") {
          const allArticles =
            articlesData?.pages.flatMap((page) => page.data) || [];
          const selectedArticle = allArticles.find((a) => a._id === value);

          if (selectedArticle) {
            newItem.priceHT = selectedArticle.priceHT;
            newItem.description = selectedArticle.label;
          }
        }

        const calc = calculateLineItem({
          priceHT: Number(newItem.priceHT) || 0,
          quantity: Number(newItem.quantity) || 0,
          vatAmount: Number(newItem.vatAmount) || 0,
          reductionAmount: Number(newItem.reductionAmount) || 0,
          reductionType: newItem.reductionType,
        });

        newItem.totalHT = calc.totalHT;
        newItem.totalTTC = calc.totalTTC;

        return newItem;
      }
      return item;
    });

    const totals = calculateFormTotals(updatedLineItems);
    setFormValues((prev) => ({
      ...prev,
      lineItems: updatedLineItems,
      totalTTC: totals.totalTTC,
      totalHT: totals.totalHT,
    }));
  };

  const handleAddPayment = (
    payment: Omit<InvoiceTransaction, "_id" | "createdAt" | "createdBy">
  ) => {
    const updatedTransactions = [
      ...(formValues.transactions || []),
      {
        ...payment,
        createdAt: new Date(),
      },
    ];

    setFormValues({
      ...formValues,
      transactions: updatedTransactions,
    });

    mutate({
      ...formValues,
      transactions: updatedTransactions,
    });
  };

  const getTotalPaid = (transactions: InvoiceTransaction[] = []): number => {
    return transactions.reduce(
      (total, transaction) => total + transaction.amount,
      0
    );
  };

  const liveSummary = useDocumentPreview(formValues.lineItems);
  const { totalHT, totalRemise, totalVAT, totalHTFinal, totalTTC } = liveSummary ?? invoice?.summary ?? calculateDisplayTotals(formValues.lineItems);
  const totalPaid = getTotalPaid(formValues.transactions);

  // Function to download PDF from server using authenticated API
  const downloadPDF = async () => {
    if (!id) return;
    
    try {
      const { getInvoiceBackend } = await import('modules/invoice/api/src/backends');
      const backend = await getInvoiceBackend();
      const pdfData = await backend.downloadInvoice(id);
      
      // Create blob and download
      const blob = new Blob([pdfData], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture-${invoice?.documentNumber.number || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const remainingBalance = totalTTC - totalPaid;
  const isPaidInFull = remainingBalance <= 0;

  return (
    <div>
      <SendEmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        quoteId={invoice?.documentNumber.number || ""}
        clientEmail={invoice?.client.email || ""}
        quoteDate={
          invoice?.createdAt
            ? new Date(invoice.createdAt).toLocaleDateString("fr-FR")
            : ""
        }
        quoteTotal={invoice?.totalTTC.toString() || ""}
        clientName={invoice?.client.name || ""}
        isQuote={false}
      />

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onAddPayment={handleAddPayment}
        invoiceTotal={totalTTC}
        totalPaid={totalPaid}
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex items-center justify-end gap-2 pt-4">
          <div
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-blue-700 cursor-pointer"
            onClick={() => setIsEmailModalOpen(true)}
          >
            <Mail className="h-4 w-4" />
            Envoyer par email
          </div>
          <div
            onClick={downloadPDF}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
          >
            <svg
              className="size-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <line x1="10" y1="9" x2="8" y2="9"></line>
            </svg>
            Télécharger
          </div>
        </div>

        {formValues._id && (
          <CardWithTitle
            title="Historique des paiements"
            actions={
              <>
                {!isPaidInFull && (
                  <button
                    type="button"
                    onClick={() => setIsPaymentModalOpen(true)}
                    className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
                  >
                    <PlusCircle className="h-4 w-4" />
                    Ajouter un paiement
                  </button>
                )}
              </>
            }
          >
            <TransactionHistory
              transactions={formValues.transactions || []}
              totalInvoiceAmount={totalTTC}
            />
          </CardWithTitle>
        )}

        <CardWithTitle title="Formulaire Facture">
          <div className="mb-5">
            <SelectDevis
              formValues={formValues}
              setFormValues={setFormValues}
            />
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <SelectClient
              formValues={formValues}
              setFormValues={setFormValues}
              data={invoice?.client}
            />
            <InputField
              variant="auth"
              label="Échéance"
              id="dueDate"
              type="date"
              extra="mb-3"
              value={
                formValues.dueDate
                  ? formValues.dueDate.toISOString().split("T")[0]
                  : ""
              }
              placeholder="Date d'échéance"
              onChange={(e) =>
                setFormValues({
                  ...formValues,
                  dueDate: e.target.value ? new Date(e.target.value) : null,
                })
              }
            />
            <SelectIntervenants
              formValues={formValues}
              setFormValues={setFormValues}
            />
            <SelectStatus
              formValues={formValues}
              setFormValues={setFormValues}
            />
          </div>

          <div className="mt-6">
            <h3 className="mb-4 text-xl font-semibold">Articles</h3>
            {formValues.lineItems.map((item, index) => (
              <div
                key={index}
                className="relative mb-4 rounded-lg border border-gray-100 bg-gray-50/50 p-4"
              >
                <button
                  type="button"
                  onClick={() => handleRemoveLineItem(index)}
                  className="absolute right-3 top-3 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7">
                  <div className="col-span-1">
                    <SelectArticle
                      value={item.articleId}
                      onChange={(articleId) =>
                        handleLineItemChange(index, "articleId", articleId)
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="Prix HT"
                      id={`priceHT-${index}`}
                      type="number"
                      extra="mb-3"
                      placeholder="0.00"
                      value={item.priceHT}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "priceHT",
                          parseFloat(e.target.value) || 0
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="Quantité"
                      id={`quantity-${index}`}
                      type="number"
                      extra="mb-3"
                      placeholder="0"
                      value={item.quantity}
                      min={1}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "quantity",
                          parseInt(e.target.value, 10) || 0
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="TVA %"
                      id={`vatAmount-${index}`}
                      type="number"
                      extra="mb-3"
                      placeholder="20.00"
                      value={item.vatAmount || ""}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "vatAmount",
                          parseFloat(e.target.value) || 0
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="Réduction"
                      id={`reductionAmount-${index}`}
                      type="number"
                      extra="mb-3"
                      placeholder="0.00"
                      value={item.reductionAmount || ""}
                      onChange={(e) =>
                        handleLineItemChange(
                          index,
                          "reductionAmount",
                          parseFloat(e.target.value) || 0
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <SelectReductionType
                      value={item.reductionType}
                      onChange={(reductionType) =>
                        handleLineItemChange(
                          index,
                          "reductionType",
                          reductionType
                        )
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <InputField
                      variant="auth"
                      label="Total TTC"
                      id={`total-${index}`}
                      type="number"
                      extra="mb-3"
                      placeholder="0.00"
                      value={item.totalTTC}
                      disabled
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <ResizableInputField
                    variant="auth"
                    label="Description"
                    id={`description-${index}`}
                    extra="mb-3"
                    placeholder="Description de l'item"
                    value={item.description}
                    onChange={(e) =>
                      handleLineItemChange(index, "description", e.target.value)
                    }
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddLineItem}
              className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-300 py-2.5 text-xs font-medium text-gray-500 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Ajouter un article
            </button>
          </div>

          <div className="mt-6 flex justify-end text-left">
            <CardWithTitle title="Totaux">
              <div className="grid w-full grid-cols-2 gap-3 sm:w-80">
                <div className="text-sm font-medium text-gray-600">
                  Total HT
                </div>
                <div className="text-right text-sm">
                  {totalHT.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €
                </div>
                <div className="text-sm font-medium text-gray-600">
                  Remise générale
                </div>
                <div className="text-right text-sm">
                  {totalRemise.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")}{" "}
                  €
                </div>
                <div className="text-sm font-medium text-gray-600">
                  Total HT Final
                </div>
                <div className="text-right text-sm">
                  {totalHTFinal
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}{" "}
                  €
                </div>
                <div className="text-sm font-medium text-gray-600">TVA</div>
                <div className="text-right text-sm">
                  {totalVAT.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €
                </div>
                <div className="text-sm font-medium text-gray-600">
                  Total TTC
                </div>
                <div className="text-right text-sm font-semibold">
                  {totalTTC.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €
                </div>

                {totalPaid > 0 && (
                  <>
                    <div className="text-sm font-medium text-gray-600">
                      Total payé
                    </div>
                    <div className="text-right text-sm font-semibold text-green-600">
                      {totalPaid
                        .toFixed(2)
                        .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}{" "}
                      €
                    </div>
                  </>
                )}

                <div className="col-span-2 mt-2 border-t pt-2">
                  <div className="flex justify-between">
                    <div className="text-base font-bold text-gray-800 dark:text-white">
                      Reste à payer
                    </div>
                    <div className={`text-right text-base font-bold ${remainingBalance <= 0 ? "text-green-600" : "text-orange-500"}`}>
                      {Math.max(0, remainingBalance)
                        .toFixed(2)
                        .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}{" "}
                      €
                    </div>
                  </div>
                </div>
              </div>
            </CardWithTitle>
          </div>
        </CardWithTitle>

        <div className="mt-2 flex w-full justify-end">
          <div className="w-full max-w-xs">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              variant="primary"
            >
              Mettre à jour la facture
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UpdateInvoice;
